import React, { useEffect, useState } from "react";
import {
  Modal,
  Table,
  Button,
  Form,
  InputNumber,
  Select,
  DatePicker,
  Space,
  message,
  Popconfirm,
} from "antd";
import moment from "dayjs";
import { ApiManager } from "../../../utils";

const api = ApiManager.getInstance();
const { RangePicker } = DatePicker;

function buildPayload(values) {
  return {
    reward_coupon_id: values.reward_coupon_id,
    min_order_amount: Math.round(Number(values.min_order_amount || 0) * 100),
    start_at: values.range?.[0]?.format("YYYY-MM-DD HH:mm:ss") || null,
    end_at: values.range?.[1]?.format("YYYY-MM-DD HH:mm:ss") || null,
    max_issues_per_user: values.max_issues_per_user || 0,
    max_total_issues: values.max_total_issues || 0,
    status: values.status != null ? values.status : 1,
  };
}

export default function PaymentRewardCampaigns({ visible, onCancel, user }) {
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const loadCampaigns = async () => {
    if (!user?.token) return;
    setLoading(true);
    try {
      const res = await api.coupons.paymentRewardCampaigns.run({
        token: user.token,
        query: { count: 50 },
      });
      if (res.result) {
        setCampaigns(res.data?.data || []);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadCoupons = async () => {
    if (!user?.token) return;
    const res = await api.coupons.list.run({
      token: user.token,
      query: { count: 100 },
    });
    if (res.result) {
      setCoupons(
        (res.data?.data || []).filter(
          (item) => item.benefit_type === "payment_reward",
        ),
      );
    }
  };

  useEffect(() => {
    if (visible) {
      loadCampaigns();
      loadCoupons();
    } else {
      setShowForm(false);
      setEditing(null);
      form.resetFields();
    }
  }, [visible, user?.token]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ status: 1 });
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({
      reward_coupon_id: row.reward_coupon_id,
      min_order_amount: (row.min_order_amount || 0) / 100,
      range:
        row.start_at && row.end_at
          ? [moment(row.start_at), moment(row.end_at)]
          : undefined,
      max_issues_per_user: row.max_issues_per_user || 0,
      max_total_issues: row.max_total_issues || 0,
      status: row.status,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    form.resetFields();
  };

  const onSubmit = async (values) => {
    const payload = buildPayload(values);
    const isEdit = Boolean(editing?.id);
    const res = isEdit
      ? await api.coupons.updatePaymentRewardCampaign.run({
          token: user.token,
          path: { id: editing.id },
          data: payload,
        })
      : await api.coupons.addPaymentRewardCampaign.run({
          token: user.token,
          data: payload,
        });

    if (res.result) {
      message.success(isEdit ? "活动已更新" : "活动已创建");
      closeForm();
      loadCampaigns();
    } else {
      message.error(res.message || "保存失败");
    }
  };

  const onToggleStatus = async (row) => {
    const res = await api.coupons.updatePaymentRewardCampaignStatus.run({
      token: user.token,
      path: { id: row.id },
      data: { status: row.status === 1 ? 0 : 1 },
    });
    if (res.result) {
      loadCampaigns();
    } else {
      message.error(res.message || "更新失败");
    }
  };

  const onDelete = async (row) => {
    const res = await api.coupons.deletePaymentRewardCampaign.run({
      token: user.token,
      path: { id: row.id },
    });
    if (res.result) {
      message.success("已删除");
      loadCampaigns();
    } else {
      message.error(res.message || "删除失败");
    }
  };

  const columns = [
    { title: "ID", dataIndex: "id", width: 70 },
    {
      title: "优惠券",
      render: (_, row) => row.coupon?.zh_name || row.reward_coupon_id,
    },
    {
      title: "门槛(元)",
      dataIndex: "min_order_amount",
      render: (value) => (value || 0) / 100,
    },
    { title: "已发放", dataIndex: "issued_count", width: 90 },
    {
      title: "状态",
      dataIndex: "status",
      width: 90,
      render: (value) => (value === 1 ? "启用" : "停用"),
    },
    {
      title: "操作",
      width: 220,
      render: (_, row) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => openEdit(row)}>
            编辑
          </Button>
          <Button type="link" size="small" onClick={() => onToggleStatus(row)}>
            {row.status === 1 ? "停用" : "启用"}
          </Button>
          <Popconfirm title="确定删除？" onConfirm={() => onDelete(row)}>
            <Button type="link" size="small" danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Modal
        open={visible}
        onCancel={onCancel}
        footer={null}
        width={960}
        title="支付有礼活动"
      >
        <Space style={{ marginBottom: 16 }}>
          <Button type="primary" onClick={openCreate}>
            新建活动
          </Button>
        </Space>
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={campaigns}
          pagination={false}
          size="small"
        />
      </Modal>
      <Modal
        open={showForm}
        onCancel={closeForm}
        footer={null}
        title={editing ? "编辑支付有礼活动" : "新建支付有礼活动"}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Form.Item
            label="支付有礼券"
            name="reward_coupon_id"
            rules={[{ required: true, message: "请选择支付有礼券" }]}
          >
            <Select
              options={coupons.map((item) => ({
                value: item.id,
                label: item.zh_name || item.ug_name,
              }))}
              placeholder="先创建支付有礼类型优惠券"
            />
          </Form.Item>
          <Form.Item
            label="订单门槛(元)"
            name="min_order_amount"
            rules={[{ required: true, message: "请输入门槛" }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="活动时间" name="range">
            <RangePicker showTime style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="每人上限(0=不限)" name="max_issues_per_user">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="总上限(0=不限)" name="max_total_issues">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="状态" name="status">
            <Select
              options={[
                { value: 1, label: "启用" },
                { value: 0, label: "停用" },
              ]}
            />
          </Form.Item>
          <Button type="primary" htmlType="submit" block>
            保存
          </Button>
        </Form>
      </Modal>
    </>
  );
}
